import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, Table, TableHead, TableRow, TableCell, TableBody,
  Paper, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, FormControl,
  InputLabel, Select, MenuItem, Snackbar, Alert, Chip, LinearProgress, Tabs, Tab,
  Card, CardContent, Grid
} from '@mui/material';
import {
  Refresh as RefreshIcon, Search as SearchIcon,
  Person as PersonIcon, AccessTime as AccessTimeIcon,
  Restaurant as RestaurantIcon, CheckCircle as CheckIcon,
  Cancel as CancelIcon, Warning as WarningIcon
} from '@mui/icons-material';
import { cafeteriaAPI, employeesAPI } from '../api/api';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  BarChart, Bar, PieChart, Pie, Cell
} from 'recharts';

const Cafeteria = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    date: '',
    meal: '',
    status: '',
    employeeId: ''
  });
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [openEntryDialog, setOpenEntryDialog] = useState(false);
  const [newEntry, setNewEntry] = useState({
    employeeId: '',
    date: '',
    meal: '',
    shuttle: false,
    notes: ''
  });
  const [employees, setEmployees] = useState([]);

  const mealTypes = ['Kahvaltı', 'Öğle Yemeği', 'Akşam Yemeği', 'Ara Öğün'];
  const statusColors = {
    'Approved': '#4caf50',
    'Rejected': '#f44336',
    'Pending': '#ff9800'
  };

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const response = await cafeteriaAPI.getAll(filters);
      setEntries(response.data || []);
    } catch (error) {
      console.error('Error fetching cafeteria entries:', error);
      showSnackbar('Yemekhane kayıtları yüklenirken hata oluştu', 'error');
    } finally {
      setLoading(false);
    }
  }, [filters, showSnackbar]);

  const fetchEmployees = useCallback(async () => {
    try {
      const response = await employeesAPI.getAll();
      setEmployees(response.data.employees || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  }, []);

  useEffect(() => {
    fetchEntries();
    fetchEmployees();
  }, [fetchEntries, fetchEmployees]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleSearch = () => {
    fetchEntries();
  };

  const handleRefresh = () => {
    fetchEntries();
  };

  const handleOpenEntryDialog = () => {
    setNewEntry({
      employeeId: '',
      date: new Date().toISOString().split('T')[0],
      meal: '',
      shuttle: false,
      notes: ''
    });
    setOpenEntryDialog(true);
  };

  const handleCloseEntryDialog = () => {
    setOpenEntryDialog(false);
    setNewEntry({
      employeeId: '',
      date: '',
      meal: '',
      shuttle: false,
      notes: ''
    });
  };

  const handleEntryChange = (field, value) => {
    setNewEntry(prev => ({ ...prev, [field]: value }));
  };

  const handleAddEntry = async () => {
    if (!newEntry.employeeId || !newEntry.date || !newEntry.meal) {
      showSnackbar('Lütfen zorunlu alanları doldurun', 'error');
      return;
    }

    try {
      await cafeteriaAPI.create(newEntry);
      showSnackbar('Yemekhane kaydı başarıyla eklendi', 'success');
      fetchEntries();
      handleCloseEntryDialog();
    } catch (error) {
      console.error('Error adding cafeteria entry:', error);
      showSnackbar('Kayıt eklenirken hata oluştu', 'error');
    }
  };

  const handleDeleteEntry = async (id) => {
    if (window.confirm('Bu kaydı silmek istediğinize emin misiniz?')) {
      try {
        await cafeteriaAPI.delete(id);
        showSnackbar('Kayıt başarıyla silindi', 'success');
        fetchEntries();
      } catch (error) {
        console.error('Error deleting entry:', error);
        showSnackbar('Kayıt silinirken hata oluştu', 'error');
      }
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Approved': 'success',
      'Rejected': 'error',
      'Pending': 'warning'
    };
    return colors[status] || 'default';
  };

  const getMealStats = () => {
    const stats = {};
    entries.forEach(entry => {
      stats[entry.meal] = (stats[entry.meal] || 0) + 1;
    });
    return Object.entries(stats).map(([name, value]) => ({ name, value }));
  };

  const getDailyStats = () => {
    const stats = {};
    entries.forEach(entry => {
      const date = entry.date?.split('T')[0];
      if (date) {
        stats[date] = (stats[date] || 0) + 1;
      }
    });
    return Object.entries(stats).map(([date, count]) => ({ date, count })).slice(-7);
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Yemekhane Yönetimi</Typography>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
        <Tab label="Kayıtlar" icon={<AccessTimeIcon />} />
        <Tab label="İstatistikler" icon={<RestaurantIcon />} />
        <Tab label="Yeni Kayıt" icon={<PersonIcon />} />
      </Tabs>

      {/* Filters */}
      {activeTab === 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={2}>
              <TextField
                label="Tarih"
                type="date"
                value={filters.date}
                onChange={(e) => handleFilterChange('date', e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Öğün</InputLabel>
                <Select
                  value={filters.meal}
                  onChange={(e) => handleFilterChange('meal', e.target.value)}
                >
                  <MenuItem value="">Tümü</MenuItem>
                  {mealTypes.map(meal => (
                    <MenuItem key={meal} value={meal}>{meal}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Durum</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <MenuItem value="">Tümü</MenuItem>
                  <MenuItem value="Approved">Onaylı</MenuItem>
                  <MenuItem value="Rejected">Reddedildi</MenuItem>
                  <MenuItem value="Pending">Beklemede</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Personel ID"
                value={filters.employeeId}
                onChange={(e) => handleFilterChange('employeeId', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={handleSearch}
                >
                  Ara
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefresh}
                >
                  Yenile
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Entries Table */}
      {activeTab === 0 && (
        <Paper sx={{ mb: 3 }}>
          {loading && <LinearProgress />}
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Tarih</TableCell>
                <TableCell>Personel</TableCell>
                <TableCell>Öğün</TableCell>
                <TableCell>Servis</TableCell>
                <TableCell>Durum</TableCell>
                <TableCell>Notlar</TableCell>
                <TableCell>İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell>{entry.date?.split('T')[0]}</TableCell>
                  <TableCell>{entry.employeeName || `${entry.firstName || ''} ${entry.lastName || ''}` || entry.employeeId}</TableCell>
                  <TableCell>{entry.meal}</TableCell>
                  <TableCell>
                    <Chip 
                      label={entry.shuttle ? 'Evet' : 'Hayır'} 
                      color={entry.shuttle ? 'primary' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={entry.status || 'Approved'} 
                      color={getStatusColor(entry.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{entry.notes}</TableCell>
                  <TableCell>
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteEntry(entry.id)}
                      size="small"
                    >
                      <CancelIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {entries.length === 0 && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">Kayıt bulunamadı</Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* Statistics */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Öğün Dağılımı</Typography>
                <PieChart width={400} height={300}>
                  <Pie
                    data={getMealStats()}
                    cx={200}
                    cy={150}
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {getMealStats().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Son 7 Gün</Typography>
                <BarChart width={400} height={300} data={getDailyStats()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" fill="#8884d8" />
                </BarChart>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* New Entry */}
      {activeTab === 2 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Yeni Kayıt Ekle</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Personel</InputLabel>
                <Select
                  value={newEntry.employeeId}
                  onChange={(e) => handleEntryChange('employeeId', e.target.value)}
                >
                  {employees.map((emp) => {
                    const employeeId = emp.id || emp.EmployeeId;
                    const displayName = 
                      emp.FirstName && emp.LastName ? `${emp.FirstName} ${emp.LastName}` :
                      emp.firstName && emp.lastName ? `${emp.firstName} ${emp.lastName}` :
                      emp.EmployeeName || `Personel ${employeeId}`;
                    
                    return (
                      <MenuItem key={employeeId} value={employeeId}>
                        {displayName} ({employeeId})
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Tarih"
                type="date"
                value={newEntry.date}
                onChange={(e) => handleEntryChange('date', e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Öğün</InputLabel>
                <Select
                  value={newEntry.meal}
                  onChange={(e) => handleEntryChange('meal', e.target.value)}
                >
                  {mealTypes.map(meal => (
                    <MenuItem key={meal} value={meal}>{meal}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notlar"
                multiline
                rows={3}
                value={newEntry.notes}
                onChange={(e) => handleEntryChange('notes', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAddEntry}
                  startIcon={<CheckIcon />}
                >
                  Kaydet
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleCloseEntryDialog}
                >
                  İptal
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Cafeteria;
